/* eslint-disable react-refresh/only-export-components -- context module: provider + hook + types live together */
import { createContext, useContext, type ReactElement, type ReactNode } from 'react';

import type { CardViewState } from './cardViewState';

/**
 * A "set all cards" broadcast (PTV2-1157). A host (the pedigree node panel header) holds this state
 * and bumps a nonce when the user picks a global view state; every `CollapsibleCard` under the
 * provider watches the nonce and adopts the state, after which each card can still be changed
 * individually until the next broadcast. Absent (context null) ⇒ cards keep purely local behavior.
 */
export interface CardBroadcast {
  /** Bumped on each global selection; 0 = no broadcast yet. */
  readonly nonce: number;
  /** View state to apply to every card at the last broadcast. */
  readonly state: CardViewState;
  /**
   * When true, `state` is FORCED (read-only / view-only mode): every card must
   * adopt it — including cards that mount later, which the nonce path misses
   * (nonce only fires on a deliberate change, so a card selected *after* the
   * lock would otherwise keep its default 'edit'). PTV2-1157.
   */
  readonly forced?: boolean;
}

const CardBroadcastContext = createContext<CardBroadcast | null>(null);

export function CardBroadcastProvider({
  value,
  children,
}: {
  readonly value: CardBroadcast;
  readonly children: ReactNode;
}): ReactElement {
  return <CardBroadcastContext.Provider value={value}>{children}</CardBroadcastContext.Provider>;
}

export function useCardBroadcast(): CardBroadcast | null {
  return useContext(CardBroadcastContext);
}
